import { useMemo, useState } from "react";
import type { FallbackTier } from "@sikhio/shared";
import {
  ALL_LAYERS,
  COMPUTED_LAYERS,
  LAYER_GROUP_LABEL,
  satelliteFreshness,
  type LayerGroup,
  type LayerId,
} from "../map/presets";

/** Per-layer health for the subset of toggles backed by a live feed. Lets the
 *  palette show *why* a layer is empty — "needs key" vs simply no features —
 *  instead of a silent dead toggle. */
export interface LayerStatus {
  tier?: FallbackTier | "loading";
  note?: string;
}

interface Props {
  enabled: Set<LayerId>;
  onToggleLayer: (id: LayerId) => void;
  /** Feature counts per layer — when present, shown next to each label so
   *  the operator immediately sees "AIS 0" vs "AIS 14" without looking at the map. */
  counts?: Partial<Record<LayerId, number>>;
  /** Feed health per layer — drives the "needs key" pill on degraded feeds. */
  statuses?: Partial<Record<LayerId, LayerStatus>>;
}

const GROUP_ORDER: LayerGroup[] = ["municipality", "agri-water", "gistda", "mobility", "incidents", "open-data", "environment", "imagery"];

export function LayerPalette({ enabled, onToggleLayer, counts, statuses }: Props) {
  // group to list of layers (preserve declaration order within each group)
  const grouped = useMemo(() => {
    const m = new Map<LayerGroup, typeof ALL_LAYERS>();
    for (const g of GROUP_ORDER) m.set(g, []);
    for (const l of ALL_LAYERS) m.get(l.group)?.push(l);
    return m;
  }, []);

  const [collapsed, setCollapsed] = useState<Record<LayerGroup, boolean>>(() => ({
    municipality: false,
    "agri-water": false,
    gistda: false,
    mobility: false,
    incidents: true,
    "open-data": true,
    environment: true,
    imagery: true,
  }));
  const toggleGroup = (g: LayerGroup) => setCollapsed((c) => ({ ...c, [g]: !c[g] }));

  const enabledByGroup = (g: LayerGroup) =>
    (grouped.get(g) ?? []).filter((l) => enabled.has(l.id)).length;

  return (
    <div className="col">
      <div>
        <div className="eyebrow" style={{ marginBottom: 6 }}>ชั้นข้อมูล</div>
        {/* MSH: the paragraph here read "เปิด/ปิดชั้นข้อมูลเชิงพื้นที่ ชั้นที่อัปเดตจาก
            API จะแสดงจำนวนรายการ" — "turn spatial layers on/off; layers fed by the
            API show a count" — directly under a heading that already says
            "ชั้นข้อมูล" (DATA LAYERS). It cost 50px of a 210px-wide column
            and repeated the heading, the toggles and the count badge that sit
            right there. The one fact it carried that the eye cannot get
            elsewhere — that the number is a feature count — is now on the
            count badge itself, which already says so in its tooltip. */}
        <div className="layer-groups">
          {GROUP_ORDER.map((g) => {
            const list = grouped.get(g) ?? [];
            if (list.length === 0) return null;
            const on = enabledByGroup(g);
            const isCollapsed = collapsed[g];
            return (
              <section key={g} className={`layer-group ${isCollapsed ? "is-collapsed" : ""}`}>
                <button
                  type="button"
                  className="layer-group-head"
                  onClick={() => toggleGroup(g)}
                  aria-expanded={!isCollapsed}
                  aria-label={`${LAYER_GROUP_LABEL[g]} layers (${on} of ${list.length} on)`}
                >
                  <span className="layer-group-name mono">{LAYER_GROUP_LABEL[g]}</span>
                  <span className="layer-group-meta mono">{on}/{list.length}</span>
                  <span className="layer-group-chevron mono">{isCollapsed ? "▸" : "▾"}</span>
                </button>
                {!isCollapsed && (
                  <div className="layer-toggles">
                    {list.map((l) => {
                      const isOn = enabled.has(l.id);
                      const status = statuses?.[l.id];
                      const needsKey = status?.tier === "unavailable";
                      const freshness = satelliteFreshness(l.id);
                      // The visible label is one line with an ellipsis, so the
                      // `title` has to carry the LABEL as well as the
                      // description — otherwise a sighted mouse user hovering a
                      // truncated row gets the description and never the name
                      // they cannot see. Measured: the title held the
                      // description for 22 of 22 rows and the label for 0.
                      const fullTitle = freshness
                        ? `${l.label} — ${l.describe}\n\nImagery date: ${freshness.date} (${freshness.label})`
                        : `${l.label} — ${l.describe}`;
                      return (
                        <button
                          type="button"
                          key={l.id}
                          className={`layer-toggle ${isOn ? "on" : "off"}`}
                          role="checkbox"
                          aria-checked={isOn}
                          title={fullTitle}
                          aria-label={`${l.label} — ${l.describe}`}
                          onClick={() => onToggleLayer(l.id)}
                        >
                          <span className="row">
                            <span className="swatch" style={{ background: l.swatch }} />
                            <span>{l.label}</span>
                          </span>
                          <span className="row" style={{ gap: 6 }}>
                            {freshness && (
                              <span className="mono caption layer-age" title={`Imagery date: ${freshness.date}`}>
                                {freshness.label}
                              </span>
                            )}
                            {/* Degraded feed (e.g. missing API key) — say so explicitly instead of a
                                silent "0" that looks like a bug. The note explains what's wrong. */}
                            {needsKey ? (
                              <span
                                className="mono caption layer-needs-key"
                                title={status?.note ?? "Feed unavailable — check API key / connectivity"}
                              >
                                needs key
                              </span>
                            ) : /* Computed/decorative layers have no feature collection — a "0" count would
                                falsely suggest a failed feed. Fall back to on/off for those. */
                            !COMPUTED_LAYERS.has(l.id) && counts != null && counts[l.id] != null ? (
                              <span
                                className={`mono caption layer-count ${counts[l.id]! > 0 ? "layer-count-ok" : "layer-count-zero"}`}
                                title={`${counts[l.id]!.toLocaleString()} features loaded`}
                              >
                                {counts[l.id]!.toLocaleString()}
                              </span>
                            ) : (
                              <span className="mono caption">{isOn ? "on" : "off"}</span>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
